import React from 'react';
import { NumericField } from './NumberInput.jsx';
import { fmtMoney } from '../engine.js';

/* v8 Path AL — PinsEditor
   Cousin of PiecewiseEditor, but for POINT-IN-TIME BALANCE PINS
   rather than payment schedules. PiecewiseEditor models a list of
   dated payments (each row is an event). PinsEditor models a list
   of ym-anchored balance assertions ("at 2026-06 this value is
   $1.5M"). Engine resolution is step-hold: latest pin where
   pin.ym ≤ forecast-ym wins.

   Used by Settings → Piecewise Assets (fixed-asset currentValue
   pins) and Piecewise LTLs (outstanding balance pins). Each row:
     - ym    (YYYY-MM month)
     - value (reporting currency)
     - note  (optional free-text context: 'Mid-year appraisal', etc.)

   `valueKey` lets callers rename the amount field (e.g. 'value' for
   assets, 'outstanding' for LTLs) so engine resolvers can keep their
   own vocabulary while this component stays one implementation. */

const PILL_SM_BASE = 'inline-flex items-center text-[10.5px] px-[7px] py-px rounded-xl border font-[inherit] tracking-[0.04em] cursor-default';
const PILL_SM = `${PILL_SM_BASE} bg-surface-raised text-text-muted border-border`;
const PILL_SM_OK = `${PILL_SM_BASE} bg-[rgba(34,197,94,0.15)] text-accent-green border-[rgba(34,197,94,0.35)]`;
const BTN_GHOST_SM = 'bg-transparent text-accent-blue border border-dashed border-border-strong px-2 py-[2px] rounded-sm font-[inherit] text-[11px] cursor-pointer self-start hover:border-accent-blue hover:bg-[rgba(96,165,250,0.08)]';
const BTN_GHOST = 'bg-transparent text-accent-blue border border-dashed border-border-strong px-[0.7rem] py-[0.45rem] rounded-sm font-[inherit] text-[0.82rem] cursor-pointer self-start hover:border-accent-blue hover:bg-[rgba(96,165,250,0.08)]';
const BTN_ICON = 'bg-surface-raised text-text-muted border border-border rounded-sm w-7 h-7 cursor-pointer font-[inherit] flex items-center justify-center p-0 hover:text-accent-red hover:border-red-soft';
const REPAY_INPUT = 'bg-surface-raised text-text border border-border rounded-sm px-2 py-[0.35rem] font-[inherit] text-[0.82rem] w-full focus:outline-none focus:border-accent-blue';
const HINT = 'mt-[0.7rem] text-[0.80rem] text-text-muted px-3 py-[0.55rem] bg-surface-el border-l-[3px] border-border-strong rounded-sm';

/* Default ym = current month as YYYY-MM */
function currentYMString() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function PinsEditor({
  label,
  defaultValue,        /* baseline balance shown as "default" context */
  pins,                /* array of { ym, value|outstanding, note? } — or null */
  onChange,            /* (nextPins | null) => void */
  valueKey = 'value',  /* 'value' for assets, 'outstanding' for LTLs */
  valueLabel = 'Value',
  hintText,            /* optional explanatory text under the rows */
}) {
  const arr = Array.isArray(pins) ? pins : [];
  const hasPins = arr.length > 0;

  const update = (idx, patch) => {
    const next = arr.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    onChange(next);
  };
  const addRow = () => {
    const last = arr[arr.length - 1];
    onChange([
      ...arr,
      {
        ym: last?.ym ?? currentYMString(),
        [valueKey]: Number(last?.[valueKey] ?? defaultValue) || 0,
        note: last?.note ?? '',
      },
    ]);
  };
  const removeRow = (idx) => {
    const next = arr.filter((_, i) => i !== idx);
    /* Setting to null (not empty array) clears the scenario key entirely
       — matches the existing "null = revert to default" convention used
       by PiecewiseEditor + recurring override reducers. */
    onChange(next.length === 0 ? null : next);
  };
  const enable = () => {
    onChange([
      { ym: currentYMString(), [valueKey]: Number(defaultValue) || 0, note: '' },
    ]);
  };
  const clear = () => onChange(null);

  /* "Latest pin wins" status: show the most recent pin's value so the
     user can confirm the step-hold resolution at-a-glance. */
  const latest = hasPins
    ? [...arr].sort((a, b) => (a.ym || '').localeCompare(b.ym || '')).at(-1)
    : null;

  return (
    <div className="border border-border rounded-[8px] px-3 py-[10px] mb-[10px] bg-white/[0.015]">
      <div className="flex items-center justify-between gap-3 mb-[6px]">
        <div className="flex items-baseline gap-[10px] flex-wrap">
          <strong className="text-text">{label}</strong>
          <span className="text-[11px] text-text-muted">
            default {fmtMoney(defaultValue, { decimals: 2 })}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {hasPins
            ? <span className={PILL_SM_OK}>pinned · latest {fmtMoney(latest?.[valueKey] ?? 0, { decimals: 2 })} @ {latest?.ym ?? '—'}</span>
            : <span className={PILL_SM}>using default</span>}
          {hasPins
            ? <button className={BTN_GHOST_SM} onClick={clear}>Reset to default</button>
            : <button className={BTN_GHOST_SM} onClick={enable}>Add pin</button>}
        </div>
      </div>
      {hasPins && (
        <div className="flex flex-col gap-[6px] mt-2">
          {arr.map((row, idx) => (
            <div key={idx} className="grid grid-cols-[110px_20px_140px_1fr_32px] gap-[0.45rem] items-center">
              <input
                type="month"
                className={REPAY_INPUT}
                value={row.ym || ''}
                onChange={(e) => update(idx, { ym: e.target.value })}
                title="Month this balance is pinned to (YYYY-MM)"
              />
              <span className="text-text-muted text-center text-[0.85rem]">$</span>
              <NumericField
                className={`${REPAY_INPUT} text-right tabular-nums`}
                value={row[valueKey] || 0}
                onChange={(v) => update(idx, { [valueKey]: v })}
                decimals={2}
              />
              <input
                type="text"
                className={REPAY_INPUT}
                value={row.note || ''}
                placeholder="Note (e.g. mid-year appraisal)"
                onChange={(e) => update(idx, { note: e.target.value })}
              />
              <button className={BTN_ICON} onClick={() => removeRow(idx)} aria-label="Remove">✕</button>
            </div>
          ))}
          <button className={BTN_GHOST} onClick={addRow}>+ Add pin</button>
          {hintText && <p className={HINT}>{hintText}</p>}
        </div>
      )}
    </div>
  );
}
